import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '@/hooks/usePermissions';
import { useGroupBalance, type GroupBalanceRow } from '@/hooks/useGroupBalance';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2, Building2 } from 'lucide-react';

const FINANCE_MODULE_QUERY_PREFIXES = [
  'financial-dashboard',
  'accounts-payable',
  'payables-stats',
  'financial-accounts',
  'expenses',
] as const;

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

export function GroupBalancePanel() {
  const { canAccess } = usePermissions();
  const queryClient = useQueryClient();
  const canView = canAccess('org:view_group_finance');

  const balanceQuery = useGroupBalance({ enabled: canView });

  const [confirmRow, setConfirmRow] = useState<GroupBalanceRow | null>(null);
  const [isSettling, setIsSettling] = useState(false);

  const handleSettleConfirm = useCallback(async () => {
    if (!confirmRow?.allocation_ids?.length) {
      setConfirmRow(null);
      return;
    }
    setIsSettling(true);
    try {
      const { error } = await (supabase as any).rpc('settle_group_balance', {
        p_allocation_ids: confirmRow.allocation_ids,
      });
      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['group-balance'] });
      await queryClient.invalidateQueries({ queryKey: ['purchase-allocations'] });
      for (const prefix of FINANCE_MODULE_QUERY_PREFIXES) {
        await queryClient.invalidateQueries({ queryKey: [prefix] });
      }

      toast.success('Liquidação registrada.');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erro ao liquidar saldo';
      toast.error(msg);
    } finally {
      setIsSettling(false);
      setConfirmRow(null);
    }
  }, [confirmRow, queryClient]);

  if (!canView) {
    return null;
  }

  const loading = balanceQuery.isLoading;

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            Saldo entre unidades (grupo)
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Valores pendentes de rateio entre empresas do mesmo grupo. A filial reconhece a obrigação perante a matriz
            (ou origem) até a liquidação.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : !balanceQuery.data?.length ? (
            <p className="text-sm text-muted-foreground">Nenhum saldo pendente entre unidades.</p>
          ) : (
            balanceQuery.data.map((row) => (
              <div
                key={`${row.company_id_from}-${row.company_id_to}`}
                className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-lg border p-3"
              >
                <div>
                  <p className="text-sm font-medium">
                    {row.to_name} deve {formatCurrency(row.pending_total)} à {row.from_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {row.allocation_ids.length} lançamento(s) pendente(s)
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="shrink-0"
                  onClick={() => setConfirmRow(row)}
                  disabled={isSettling}
                >
                  Liquidar
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmRow} onOpenChange={(open) => !open && !isSettling && setConfirmRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar liquidação</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-left">
              {confirmRow && (
                <>
                  <span className="font-medium text-foreground">{confirmRow.to_name}</span> quita{' '}
                  <span className="font-semibold text-foreground">{formatCurrency(confirmRow.pending_total)}</span> em
                  favor de <span className="font-medium text-foreground">{confirmRow.from_name}</span>.
                  <br />
                  <br />
                  Serão liquidados {confirmRow.allocation_ids.length} rateio(s) pendente(s). Os títulos de contas a
                  pagar correspondentes na filial serão baixados como pagos, quando existirem.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSettling}>Cancelar</AlertDialogCancel>
            <Button type="button" disabled={isSettling} onClick={() => void handleSettleConfirm()}>
              {isSettling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirmar liquidação
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
